import React, { useState, useCallback } from 'react';
import './ImageCropper.css';

function ImageCropper({ image, onCropComplete, onCancel, aspectRatio = 1 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageElement, setImageElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleImageLoad = (e) => {
    setImageElement(e.target);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - crop.x, y: e.clientY - crop.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setCrop({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const createCroppedImage = useCallback(async () => {
    if (!imageElement) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const size = 256; // Output size (matches circular overlay)
    canvas.width = size;
    canvas.height = size;

    // Calculate the actual displayed size of the image
    const displayedWidth = imageElement.width;
    const displayedHeight = imageElement.height;
    
    // Calculate the size of the crop area in displayed pixels (matches the 256px circle overlay)
    const cropDisplaySize = 256 / zoom;
    
    // Convert displayed pixels to source image pixels
    const scaleX = imageElement.naturalWidth / displayedWidth;
    const scaleY = imageElement.naturalHeight / displayedHeight;
    
    // Calculate crop position (centered + user offset)
    const cropSourceSize = Math.min(
      cropDisplaySize * scaleX,
      cropDisplaySize * scaleY
    );
    const cropX = (imageElement.naturalWidth - cropSourceSize) / 2 - (crop.x * scaleX / zoom);
    const cropY = (imageElement.naturalHeight - cropSourceSize) / 2 - (crop.y * scaleY / zoom);

    // Draw the cropped area to canvas
    ctx.drawImage(
      imageElement,
      cropX,
      cropY,
      cropSourceSize,
      cropSourceSize,
      0,
      0,
      size,
      size
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  }, [imageElement, crop, zoom]);

  const handleSave = async () => {
    const croppedImage = await createCroppedImage();
    onCropComplete(croppedImage);
  };

  return (
    <div className="image-cropper-backdrop">
      <div className="image-cropper-modal">
        <div className="cropper-header">
          <h3>Crop Image</h3>
          <button className="cropper-close" onClick={onCancel}>×</button>
        </div>
        
        <div className="cropper-content">
          <div 
            className="crop-container"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <img 
              src={image} 
              alt="Crop preview" 
              onLoad={handleImageLoad}
              draggable={false}
              style={{ 
                transform: `translate(${crop.x}px, ${crop.y}px) scale(${zoom})`,
                pointerEvents: 'none',
                userSelect: 'none'
              }}
            />
            <div className="crop-overlay"></div>
          </div>
          
          <div className="cropper-controls">
            <label>Zoom</label>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="zoom-slider"
            />
          </div>
        </div>

        <div className="cropper-footer">
          <button className="btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="btn-save" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
